import { FC } from 'react';
import styled from 'styled-components';

import { TableTD, TableTR } from 'dashboard/layouts/TableLayout';
import { LIST_TYPE_NAMES } from 'dashboard/lib/constants/list';
import { VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { ConnectionNode, PlatformConfigType } from 'dashboard/models/platform-node';
import { REPORT_TEMPLATE_TYPE_TO_LABEL } from 'dashboard/models/report-template';
import { NODE_TYPE_TO_LABEL } from 'dashboard/views/DashboardPlatformConnectionsShowView/constants';
import { PlatformConnectionsDirection } from 'dashboard/views/DashboardPlatformConnectionsShowView/DashboardPlatformConnectionsShowView';
import NullState from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/NullState';
import { PlatformNodeLink } from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/PlatformNodeLink';
import {
  PlatformNodeTable,
  PlatformNodeTableHeader,
} from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/PlatformNodeTable';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';

const StyledCardTitle = styled(Card.Title)`
  margin: 0 !important;
`;

interface TableConnectionNodeProps {
  connectionsDirection: PlatformConnectionsDirection;
  description: string;
  displayConnectedBy: boolean;
  nodes: ConnectionNode[];
  nodeTokenToNode: Record<string, ConnectionNode>;
  rootNodeIsLatestPublishedVersion?: boolean;
  tableName: string;
}

const TableConnectionNode: FC<TableConnectionNodeProps> = ({
  connectionsDirection,
  description,
  displayConnectedBy,
  nodes,
  nodeTokenToNode,
  rootNodeIsLatestPublishedVersion,
  tableName,
}) => {
  return (
    <Card>
      <Card.Body>
        <StyledCardTitle>{tableName}</StyledCardTitle>
        <Card.Subtitle>{description}</Card.Subtitle>
        <PlatformNodeTable>
          <PlatformNodeTableHeader
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'id', label: 'ID' },
              { key: 'type', label: 'Type' },
              ...(displayConnectedBy ? [{ key: 'connected_by', label: 'Connected By' }] : []),
            ]}
          />
          {nodes.map((connectionNode, idx) => {
            return (
              <TableTR disableHoverBackground key={idx}>
                <TableTD>
                  {connectionNode['permission-blocked'] ? (
                    <Sticker variant='grey'>Restricted</Sticker>
                  ) : (
                    <PlatformNodeLink node={connectionNode} />
                  )}
                </TableTD>
                <TableTD>{connectionNode.token}</TableTD>
                <TableTD>
                  {!connectionNode['permission-blocked'] && (
                    <>
                      {(PlatformConfigType.VerificationTemplateVersion === connectionNode.type ||
                        PlatformConfigType.VerificationTemplate === connectionNode.type) &&
                        VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT[connectionNode.metadata.type]}
                      {PlatformConfigType.List === connectionNode.type &&
                        LIST_TYPE_NAMES[connectionNode.metadata.type]}
                      {PlatformConfigType.ReportTemplate === connectionNode.type &&
                        REPORT_TEMPLATE_TYPE_TO_LABEL[connectionNode.metadata.type]}{' '}
                    </>
                  )}
                  {NODE_TYPE_TO_LABEL[connectionNode.type]}
                </TableTD>
                {displayConnectedBy && !connectionNode['permission-blocked'] && (
                  <TableTD>
                    {connectionNode[
                      connectionsDirection === 'outbound'
                        ? 'inbound-connections'
                        : 'outbound-connections'
                    ]?.map((token, index) => (
                      <span key={index}>
                        <PlatformNodeLink node={nodeTokenToNode[token]} key={token} />
                        <br />
                      </span>
                    ))}
                  </TableTD>
                )}
              </TableTR>
            );
          })}
        </PlatformNodeTable>
        {!nodes.length && (
          <NullState
            message={
              !rootNodeIsLatestPublishedVersion && connectionsDirection === 'inbound'
                ? 'Connections are only available for the latest published version'
                : `No ${tableName} Found`
            }
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default TableConnectionNode;
